import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { Box, css } from '@terra/ui-styled-system';
import React, { forwardRef, useEffect, useState } from 'react';
import { OnChangesPlugin } from './plugins/OnChangePlugin';
import { MentionsTriggerPlugin } from './plugins/MentionsTriggerPlugin';
import { SlashCommandsAndShortcutsPlugin } from './plugins/SlashCommandsAndShortcutsPlugin';
import type { SlashCommandItem } from '../../slash-commands';

const PLACEHOLDER_TEXT = 'Ask me anything or type @ to mention...';

const PADDING_VALUE = 1;

const editorStyles = css({
  position: 'relative',
  borderRadius: 'md',
  padding: PADDING_VALUE,
  minHeight: '28px',
  maxHeight: '80px',
  overflowY: 'auto',
  backgroundColor: 'transparent',
  fontSize: '13px',
  '& .editor-paragraph': {
    margin: 0,
    position: 'relative',
    lineHeight: 1.4,
    fontSize: '13px',
  },
  '& .editor-text': {
    color: 'fg.default',
    fontSize: '13px',
  },
});

const copilotEditorStyles = css({
  position: 'relative',
  borderRadius: 'md',
  padding: PADDING_VALUE,
  minWidth: 0,
  width: '100%',
  minHeight: '28px',
  maxHeight: '200px',
  overflowX: 'hidden',
  overflowY: 'auto',
  backgroundColor: 'transparent',
  fontSize: 'sm',
  '& .editor-paragraph': {
    margin: 0,
    position: 'relative',
    lineHeight: 'snug',
    fontSize: 'sm',
    overflowWrap: 'anywhere',
  },
  '& .editor-text': {
    color: 'fg.default',
    fontSize: 'sm',
  },
});

const placeholderStyles = css({
  position: 'absolute',
  insetInlineStart: PADDING_VALUE,
  insetBlockStart: PADDING_VALUE,
  insetInlineEnd: PADDING_VALUE,
  color: 'fg.muted',
  opacity: 0.5,
  pointerEvents: 'none',
  userSelect: 'none',
  fontSize: '13px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const copilotPlaceholderStyles = css({
  position: 'absolute',
  insetInlineStart: PADDING_VALUE,
  insetBlockStart: PADDING_VALUE,
  insetInlineEnd: PADDING_VALUE,
  color: 'fg.muted',
  opacity: 1,
  pointerEvents: 'none',
  userSelect: 'none',
  fontSize: 'sm',
  lineHeight: 'snug',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const contentEditableStyles = css({
  outline: 'none',
  boxShadow: 'none',
  width: '100%',
  height: '100%',
  '&:focus, &:focus-visible, &[data-focus-visible]': {
    outline: 'none',
    boxShadow: 'none',
  },
});

export interface EditorTextAreaRef {
  nodeRef?: React.RefObject<HTMLDivElement>;
}

interface EditorBodyProps {
  editorKey: number;
  onSubmit?: () => void;
  onStop?: () => void;
  onCycleMode?: () => void;
  slashCommands?: readonly SlashCommandItem[];
  onMentionsTrigger?: (position?: { x: number; y: number }) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  variant?: 'default' | 'copilot';
}

export const EditorTextArea = forwardRef<EditorTextAreaRef, EditorBodyProps>(
  (
    {
      editorKey,
      onSubmit,
      onStop,
      onCycleMode,
      slashCommands,
      onMentionsTrigger,
      isLoading,
      isDisabled,
      placeholder = PLACEHOLDER_TEXT,
      variant = 'default',
    },
    _ref,
  ) => {
    const [editor] = useLexicalComposerContext();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [content, setContent] = useState('');

    useEffect(() => {
      editor.setEditable(!isDisabled);

      return () => {
        editor.setEditable(true);
      };
    }, [editor, isDisabled]);

    return (
      <Box
        className={variant === 'copilot' ? copilotEditorStyles : editorStyles}
        key={editorKey}
      >
        <PlainTextPlugin
          contentEditable={
            <ContentEditable
              aria-placeholder={placeholder}
              aria-disabled={isDisabled}
              className={contentEditableStyles}
              contentEditable={!isDisabled}
              spellCheck
              tabIndex={isDisabled ? -1 : undefined}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (isDisabled) {
                  e.preventDefault();

                  return;
                }

                if (e.key === 'Enter' && !e.shiftKey) {
                  if (e.defaultPrevented) {
                    return;
                  }

                  e.preventDefault();
                  onSubmit?.();
                }
              }}
            />
          }
          placeholder={
            <Box
              className={
                variant === 'copilot'
                  ? copilotPlaceholderStyles
                  : placeholderStyles
              }
            >
              {isLoading ? '' : placeholder}
            </Box>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <AutoFocusPlugin />
        <HistoryPlugin />
        <MentionsTriggerPlugin onMentionsTrigger={onMentionsTrigger} />
        <SlashCommandsAndShortcutsPlugin
          slashCommands={slashCommands}
          isLoading={isLoading}
          isDisabled={isDisabled}
          onStop={onStop}
          onCycleMode={onCycleMode}
        />
        <OnChangesPlugin setContent={setContent} />
      </Box>
    );
  },
);
EditorTextArea.displayName = 'EditorTextArea';
